import { Component } from '@theme/component';

const WHEEL_GESTURES_URL = 'https://unpkg.com/embla-carousel-wheel-gestures@8.1.0/dist/embla-carousel-wheel-gestures.umd.js';

/** @type {Promise<any> | null} */
let wheelGesturesPromise = null;

/**
 * Lazily load the WheelGestures plugin (singleton).
 * @returns {Promise<any>}
 */
const loadWheelGestures = () => {
  if (!wheelGesturesPromise) {
    wheelGesturesPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = WHEEL_GESTURES_URL;
      script.onload = () => resolve(/** @type {any} */(window).EmblaCarouselWheelGestures);
      script.onerror = () => reject(new Error('Failed to load wheel-gestures plugin'));
      document.head.appendChild(script);
    });
  }
  return wheelGesturesPromise;
};

/**
 * @typedef {object} Refs
 * @property {HTMLElement} viewport – The Embla viewport element.
 * @property {HTMLElement} container – The Embla container element.
 * @property {HTMLElement[]} slides – The Embla slide elements.
 * @property {HTMLButtonElement} prevButton – The previous button.
 * @property {HTMLButtonElement} nextButton – The next button.
 * @property {HTMLElement} pagination – The pagination container.
 * @property {HTMLButtonElement[]} dots – The pagination dot buttons.
 * @property {HTMLElement} currentIndex – The current index indicator element (for numeric pagination like [1/4]).
 */

/**
 * A reusable slider component using Embla Carousel.
 *
 * @extends Component<Refs>
 */
class SliderComponent extends Component {
  /** @type {any} */
  #embla = null;

  get embla() {
    return this.#embla;
  }

  async connectedCallback() {
    super.connectedCallback();
    await this.#initEmbla();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#embla) {
      this.#embla.destroy();
      this.#embla = null;
    }
  }

  /**
   * Initialise Embla Carousel
   */
  async #initEmbla() {
    // Détruire l'instance existante si elle existe
    if (this.#embla) {
      this.#embla.destroy();
      this.#embla = null;
    }

    const { viewport, container, prevButton, nextButton } = this.refs;
    if (!viewport || !container) return;

    // Vérifier que EmblaCarousel est disponible (chargé depuis le CDN)
    const EmblaCarousel = /** @type {any} */ (window).EmblaCarousel;
    if (typeof EmblaCarousel === 'undefined') {
      console.warn('EmblaCarousel is not loaded yet');
      return;
    }

    // Récupérer les options depuis les attributs
    const align = this.getAttribute('align') || 'start';
    const containScroll = this.getAttribute('contain-scroll') || 'trimSnaps';
    const dragFree = this.getAttribute('drag-free') === 'true';
    const draggable = this.getAttribute('draggable') !== 'false'; // Permet de désactiver complètement le drag
    const slidesToScroll = parseInt(this.getAttribute('slides-to-scroll') || '1', 10);
    const loop = this.getAttribute('loop') === 'true';
    const breakpoints = this.#parseBreakpoints();

    // Déterminer la valeur active par défaut basée sur les breakpoints
    let active = true;
    if (breakpoints && Object.keys(breakpoints).length > 0) {
      // Si on a un breakpoint qui active à 750px, alors active par défaut est false
      const hasDesktopBreakpoint = Object.keys(breakpoints).some(
        (key) => key.includes('750px') && breakpoints[key].active === true
      );
      if (hasDesktopBreakpoint) {
        active = false;
      }
    }

    // Charger le plugin wheel-gestures à la demande
    const plugins = [];
    if (this.getAttribute('wheel-scroll') === 'true') {
      try {
        const WheelGestures = await loadWheelGestures();
        plugins.push(WheelGestures());
      } catch (error) {
        console.warn('Could not load wheel-gestures plugin:', error);
      }
    }

    this.#embla = EmblaCarousel(viewport, {
      active,
      align,
      containScroll,
      dragFree,
      draggable,
      loop,
      slidesToScroll,
      breakpoints,
    }, plugins);

    // Gérer les boutons de navigation
    this.#setupNavigation();

    // Gérer la pagination (dots)
    this.#setupPagination();

    // Gérer la pagination numérique [1/n]
    this.#setupNumericPagination();
  }

  /**
   * Configure les boutons de navigation
   */
  #setupNavigation() {
    const { prevButton, nextButton } = this.refs;
    if (!prevButton || !nextButton || !this.#embla) return;

    const updateButtonStates = () => {
      if (!this.#embla) return;

      const scrollProgress = this.#embla.scrollProgress();
      // Utiliser scrollProgress avec tolérance car canScrollNext/Prev
      // peut avoir des problèmes de précision en virgule flottante
      const TOLERANCE = 0.001;
      const isAtStart = scrollProgress <= TOLERANCE;
      const isAtEnd = scrollProgress >= 1 - TOLERANCE;

      prevButton.disabled = isAtStart;
      nextButton.disabled = isAtEnd;
    };

    // Mettre à jour l'état des boutons sur tous les événements pertinents
    this.#embla.on('init', updateButtonStates);
    this.#embla.on('select', updateButtonStates);
    this.#embla.on('settle', updateButtonStates);
    this.#embla.on('reInit', updateButtonStates);
    updateButtonStates();
  }

  /**
   * Gère le clic sur le bouton précédent
   */
  handlePrevClick() {
    if (this.#embla) {
      this.#embla.scrollPrev();
    }
  }

  /**
   * Gère le clic sur le bouton suivant
   */
  handleNextClick() {
    if (this.#embla) {
      this.#embla.scrollNext();
    }
  }

  /**
   * Configure la pagination avec les dots
   */
  #setupPagination() {
    const { dots } = this.refs;
    if (!dots || dots.length === 0 || !this.#embla) return;

    const activeClass = this.getAttribute('dot-active-class') || 'transformation-showcase__dot--active';

    const updateDots = () => {
      if (!this.#embla) return;
      const selectedIndex = this.#embla.selectedScrollSnap();

      dots.forEach((dot, index) => {
        dot.classList.toggle(activeClass, index === selectedIndex);
      });
    };

    // Mettre à jour les dots sur les événements Embla
    this.#embla.on('init', updateDots);
    this.#embla.on('select', updateDots);
    this.#embla.on('reInit', updateDots);
    updateDots();
  }

  /**
   * Gère le clic sur un dot de pagination
   * @param {number} index - L'index de la slide à afficher
   */
  handleDotClick(index) {
    if (this.#embla) {
      this.#embla.scrollTo(index);
    }
  }

  /**
   * Configure la pagination numérique [1/n] via ref="currentIndex"
   */
  #setupNumericPagination() {
    const { currentIndex } = this.refs;
    if (!currentIndex || !this.#embla) return;

    const updateCurrentIndex = () => {
      if (!this.#embla) return;
      const selectedIndex = this.#embla.selectedScrollSnap();
      currentIndex.textContent = String(selectedIndex + 1);
    };

    // Mettre à jour l'index sur les événements Embla
    this.#embla.on('init', updateCurrentIndex);
    this.#embla.on('select', updateCurrentIndex);
    this.#embla.on('reInit', updateCurrentIndex);
    updateCurrentIndex();
  }

  /**
   * Navigate to previous slide
   * Called via on:click="/prevSlide"
   * Wraps to last slide if at the beginning
   */
  prevSlide() {
    if (!this.#embla) return;

    if (this.#embla.canScrollPrev()) {
      this.#embla.scrollPrev();
    } else {
      // Wrap to last slide
      const lastIndex = this.#embla.scrollSnapList().length - 1;
      this.#embla.scrollTo(lastIndex);
    }
  }

  /**
   * Navigate to next slide
   * Called via on:click="/nextSlide"
   * Wraps to first slide if at the end
   */
  nextSlide() {
    if (!this.#embla) return;

    if (this.#embla.canScrollNext()) {
      this.#embla.scrollNext();
    } else {
      // Wrap to first slide
      this.#embla.scrollTo(0);
    }
  }

  /**
   * Parse breakpoints from data-breakpoints attribute
   * @returns {Record<string, any>}
   */
  #parseBreakpoints() {
    const breakpointsAttr = this.getAttribute('data-breakpoints');
    if (!breakpointsAttr) return {};

    try {
      return JSON.parse(breakpointsAttr);
    } catch (error) {
      console.warn('Invalid breakpoints JSON:', error);
      return {};
    }
  }

  /**
   * Réinitialise le slider (utile après un morph)
   */
  async reInit() {
    await this.#initEmbla();
  }
}

if (!customElements.get('slider-component')) {
  customElements.define('slider-component', SliderComponent);
}

